import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { EagleLogo } from "@/components/EagleLogo";
import { ChevronRight, Hammer, ShieldCheck, ClipboardCheck, LogOut, Mail, Lock } from "lucide-react";
import { api, API, describeRequestError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { ROLE_LABEL } from "@/lib/util";

const OAUTH_ERROR_MESSAGES = {
    oauth_failed: "Google sign-in failed. Please try again or use email/password.",
    email_unverified: "That Google account's email isn't verified. Please use a verified account or sign in with email/password.",
};

export default function LoginPage() {
    const navigate = useNavigate();
    const { user, employee, logout, loading, refresh } = useAuth();

    const [mode, setMode] = useState("login"); // "login" | "forgot"
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState(null);
    const [info, setInfo] = useState(null);

    useEffect(() => {
        const oauthError = new URLSearchParams(window.location.search).get("error");
        if (oauthError) {
            setErr(OAUTH_ERROR_MESSAGES[oauthError] || "Sign-in failed. Please try again.");
            window.history.replaceState({}, "", window.location.pathname);
        }
    }, []);

    const goHome = (role) => {
        navigate(role === "admin" ? "/admin" : role === "manager" ? "/foreman" : "/crew", { replace: true });
    };

    const handleSignIn = () => {
        // Full page navigation (not an axios call) -- Google requires a top-level
        // redirect for the OAuth consent screen. The backend builds the Google
        // authorization URL and redirects there; see /api/auth/google/login.
        //
        // Passing our own origin is what lets a non-production deployment get
        // its own session back: without it the backend always returns to
        // FRONTEND_URL, so signing in from a preview logs you into production
        // instead. The backend accepts it only if it is on the CORS allowlist.
        const origin = encodeURIComponent(window.location.origin);
        window.location.href = `${API}/auth/google/login?origin=${origin}`;
    };

    const submitLogin = async (e) => {
        e.preventDefault();
        setErr(null); setInfo(null); setBusy(true);
        try {
            const r = await api.post("/auth/login-password", { email: email.trim().toLowerCase(), password });
            await refresh();
            goHome(r.data.employee?.role);
        } catch (e2) {
            setErr(describeRequestError(e2, "Sign-in failed."));
        } finally { setBusy(false); }
    };

    const submitForgot = async (e) => {
        e.preventDefault();
        setErr(null); setInfo(null); setBusy(true);
        try {
            const r = await api.post("/auth/forgot-password", { email: email.trim().toLowerCase() });
            setInfo(r.data?.message || "If an account exists, a link has been sent.");
        } catch (e2) {
            setErr(describeRequestError(e2, "Could not send the link."));
        } finally { setBusy(false); }
    };

    return (
        <div className="min-h-screen flex flex-col" data-testid="login-page">
            {/* Header */}
            <div className="bg-brand-charcoal text-brand-bone">
                <div className="max-w-5xl mx-auto px-6 py-10 sm:py-16 flex items-center gap-5">
                    <EagleLogo className="h-16 w-16" />
                    <div>
                        <div className="text-[11px] uppercase tracking-[0.3em] text-brand-gold">Talon Construction Company</div>
                        <h1 className="text-4xl sm:text-5xl text-brand-bone mt-1 leading-tight">
                            The <span className="font-bold italic">Nest</span>
                        </h1>
                        <p className="mt-2 text-brand-bone/80 max-w-xl text-sm sm:text-base">
                            Track time. Stay safe. Build better.
                        </p>
                    </div>
                </div>
            </div>

            <main className="flex-1 paper-bg">
                <div className="max-w-5xl mx-auto px-6 py-10 grid sm:grid-cols-2 gap-8 items-start">
                    {/* Sign-in card */}
                    <div className="bg-white rounded-2xl border border-brand-charcoal/15 shadow-card p-6 sm:p-8">
                        {!loading && user && employee && (
                            <div className="mb-5 p-4 rounded-xl bg-brand-navy/10 border border-brand-navy/30" data-testid="already-signed-in-banner">
                                <div className="text-[10px] uppercase tracking-widest text-brand-navy">You&#39;re signed in</div>
                                <div className="font-bold text-lg mt-1 text-brand-charcoal">
                                    {employee.name} &middot; <span className="text-brand-navy">{ROLE_LABEL[employee.role]}</span>
                                </div>
                                <div className="text-xs text-brand-charcoal/60 mt-0.5 truncate">{user.email}</div>
                                <div className="flex gap-2 mt-3">
                                    <button onClick={() => goHome(employee.role)} className="flex-1 h-11 rounded-xl bg-brand-navy hover:bg-brand-navy-dark text-brand-bone font-bold text-xs uppercase tracking-widest" data-testid="continue-to-area-button">
                                        Continue as {ROLE_LABEL[employee.role]}
                                    </button>
                                    <button onClick={logout} className="h-11 px-3 rounded-xl bg-brand-stone hover:bg-brand-gold/40 text-brand-charcoal font-bold text-xs uppercase tracking-widest inline-flex items-center gap-1.5" data-testid="signout-from-login">
                                        <LogOut className="h-4 w-4" /> Sign out
                                    </button>
                                </div>
                                <p className="text-[11px] text-brand-charcoal/60 mt-2">Sign out to switch accounts.</p>
                            </div>
                        )}

                        <h2 className="font-bold text-2xl mb-2">Sign in</h2>
                        <p className="text-brand-charcoal/70 text-sm mb-6">
                            Your role (CREW / FOREMAN / ADMIN) is set by the office.
                        </p>

                        <button
                            data-testid="google-signin-button"
                            onClick={handleSignIn}
                            className="w-full h-14 bg-brand-navy hover:bg-brand-navy-dark text-brand-bone rounded-xl font-bold text-base flex items-center justify-center gap-3 shadow-card transition-colors"
                        >
                            Continue with Google
                            <ChevronRight className="h-5 w-5" />
                        </button>

                        <div className="flex items-center gap-3 my-5">
                            <div className="h-px flex-1 bg-brand-charcoal/15" />
                            <span className="text-[11px] uppercase tracking-widest text-brand-charcoal/50">or use email</span>
                            <div className="h-px flex-1 bg-brand-charcoal/15" />
                        </div>

                        {err && <div className="mb-3 text-sm text-brand-danger bg-brand-danger/10 border border-brand-danger/30 rounded-lg px-3 py-2" data-testid="auth-form-error">{err}</div>}
                        {info && <div className="mb-3 text-sm text-brand-success bg-brand-success/10 border border-brand-success/30 rounded-lg px-3 py-2" data-testid="auth-form-info">{info}</div>}

                        {mode === "login" ? (
                            <form onSubmit={submitLogin} className="space-y-3" data-testid="email-login-form">
                                <div className="relative">
                                    <Mail className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-brand-charcoal/40" />
                                    <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" className="w-full h-12 pl-10 pr-3 border border-brand-charcoal/20 rounded-xl" data-testid="login-email-input" />
                                </div>
                                <div className="relative">
                                    <Lock className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-brand-charcoal/40" />
                                    <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password" className="w-full h-12 pl-10 pr-3 border border-brand-charcoal/20 rounded-xl" data-testid="login-password-input" />
                                </div>
                                <button type="submit" disabled={busy} className="w-full h-12 bg-brand-charcoal hover:bg-brand-charcoal/90 text-brand-bone rounded-xl font-bold disabled:opacity-60" data-testid="email-login-submit">
                                    {busy ? "Signing in…" : "Sign in with email"}
                                </button>
                                <button type="button" onClick={() => { setMode("forgot"); setErr(null); setInfo(null); }} className="w-full text-center text-sm text-brand-navy underline underline-offset-4" data-testid="forgot-password-link">
                                    Forgot or need to set your password?
                                </button>
                            </form>
                        ) : (
                            <form onSubmit={submitForgot} className="space-y-3" data-testid="forgot-password-form">
                                <p className="text-sm text-brand-charcoal/70">Enter your work email and we&#39;ll send a link to set your password.</p>
                                <div className="relative">
                                    <Mail className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-brand-charcoal/40" />
                                    <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" className="w-full h-12 pl-10 pr-3 border border-brand-charcoal/20 rounded-xl" data-testid="forgot-email-input" />
                                </div>
                                <button type="submit" disabled={busy} className="w-full h-12 bg-brand-charcoal hover:bg-brand-charcoal/90 text-brand-bone rounded-xl font-bold disabled:opacity-60" data-testid="forgot-submit">
                                    {busy ? "Sending…" : "Send reset link"}
                                </button>
                                <button type="button" onClick={() => { setMode("login"); setErr(null); setInfo(null); }} className="w-full text-center text-sm text-brand-navy underline underline-offset-4" data-testid="back-to-login-link">
                                    Back to sign in
                                </button>
                            </form>
                        )}
                    </div>

                    {/* Role cards */}
                    <div className="space-y-4">
                        {[
                            { icon: Hammer, title: "CREW", body: "Add your hours each day — it takes a minute and you won't have to remember Sunday. Your week must be sent in by Sunday 11:59 PM." },
                            { icon: ClipboardCheck, title: "FOREMAN", body: "Approve hours as they come in through the week. Last week's timecards must all be approved by Monday 11:59 PM." },
                            { icon: ShieldCheck, title: "ADMIN", body: "Invite people, set up projects, export payroll. Controls and rules baked in." },
                        ].map((c) => (
                            <div key={c.title} className="bg-white/70 backdrop-blur rounded-xl border border-brand-charcoal/10 p-5 flex gap-4 items-start">
                                <div className="h-10 w-10 rounded-lg bg-brand-navy/10 text-brand-navy flex items-center justify-center shrink-0">
                                    <c.icon className="h-5 w-5" />
                                </div>
                                <div>
                                    <div className="font-bold tracking-widest text-xs text-brand-navy">{c.title}</div>
                                    <p className="text-sm text-brand-charcoal/80 mt-1">{c.body}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </main>

            <footer className="bg-brand-charcoal/95 text-brand-bone/70 text-xs">
                <div className="max-w-5xl mx-auto px-6 py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                    <div>&copy; Talon Construction Company &mdash; Field Operations</div>
                    <div className="tracking-widest uppercase">Pay weekly &middot; Crew submit Sun 11:59 PM &middot; Foreman approve Mon 11:59 PM PT</div>
                </div>
            </footer>
        </div>
    );
}
